import { useCallback } from 'react'

/**
 * Custom hook for managing ViewRecord API calls
 * Handles marking individual and bulk records as seen
 */
const useViewRecords = (userStore) => {
  // Extract the activity type and ID from our composite activity IDs  
  const parseActivityId = useCallback((activityId) => {
    // Our activity IDs are like "journal-123", "task-456", etc.
    const [type, id] = activityId.split('-')
    
    const typeMapping = {
      journal: 'JournalEntry',
      task: 'Task', 
      goal: 'Goal',
      strategy: 'Strategy',
      priority: 'Priority',
      comment: 'Comment'
    }
    
    return {
      viewable_type: typeMapping[type],
      viewable_id: parseInt(id, 10)
    }
  }, [])

  // Mark a single activity as seen
  const markAsSeen = useCallback(async (activityId) => {
    if (!activityId || !userStore) return

    try {
      const { viewable_type, viewable_id } = parseActivityId(activityId)
      
      if (!viewable_type || !viewable_id) {
        console.warn('Invalid activity ID format:', activityId)
        return
      }

      const response = await fetch('/api/v1/view_records', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || ''
        },
        body: JSON.stringify({
          view_record: {
            viewable_type,
            viewable_id
          }
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      return data
    } catch (error) {
      console.error('Error marking activity as seen:', error)
      throw error
    }
  }, [userStore, parseActivityId])

  // Mark multiple activities as seen
  const markMultipleAsSeen = useCallback(async (activityIds) => {
    if (!activityIds || !Array.isArray(activityIds) || activityIds.length === 0 || !userStore) return

    try {
      const items = activityIds
        .map(parseActivityId)
        .filter(item => item.viewable_type && item.viewable_id)

      if (items.length === 0) {
        return
      }

      const response = await fetch('/api/v1/view_records/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json', 
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || ''
        },
        body: JSON.stringify({ items })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.errors && data.errors.length > 0) {
        console.warn('Some items failed to mark as seen:', data.errors)
      }
      
      return data
    } catch (error) {
      console.error('Error marking multiple activities as seen:', error)
      throw error
    }
  }, [userStore, parseActivityId])

  return {
    markAsSeen,
    markMultipleAsSeen
  }
}

export default useViewRecords
